import type {
  DataAccessRequestGenotypicQuery,
  DataAccessRequestPhenotypicQuery,
  DataAccessRequestQuerySource
} from 'services/results/types'

export interface DataAccessRequestQueryDisplayRow {
  /** Source field, so a renderer can treat a row specially (SQL opens in a modal). */
  field: string
  label: string
  value: string
}

/** SQL is too long to sit inline in the metadata list; it is shown in a modal instead. */
export const SQL_QUERY_FIELD = 'sqlQuery'

export const NO_RESTRICTION_APPLIED_MESSAGE = 'No restriction was applied'

export const PARTICIPANTS_MATCHING_QUERY_LABEL = 'Participants matching query'
export const PARTICIPANTS_IN_DATASET_LABEL = 'Participants in dataset'

type QueryParameterValue = string | string[] | undefined

/**
 * Opaque pass-through objects (`query`, `queryView`, `phenotypeFilters`, `criteria`) have no
 * Airlock-specific renderer, so they are omitted from these labelled rows. The full `query`
 * object is rendered separately via PreviewQuery on the form card.
 *
 * `participantsAppliedType` is mapped from the cohort-browser `AppliedQueryType` enum to a
 * human label; unknown values are left out rather than shown as raw internals.
 */
type DisplayedPhenotypicField = Extract<
  keyof DataAccessRequestPhenotypicQuery,
  'naturalLanguageQuery' | 'participantsAppliedType' | 'sqlQuery'
>

type DisplayedGenotypicField = Extract<
  keyof DataAccessRequestGenotypicQuery,
  'filterJobName' | 'queryMarkers' | 'genotypes' | 'filterJobId'
>

const PHENOTYPIC_FIELD_LABELS: Record<DisplayedPhenotypicField, string> = {
  naturalLanguageQuery: 'Natural language query',
  participantsAppliedType: 'Query type',
  sqlQuery: 'SQL query'
}

const PHENOTYPIC_FIELD_ORDER: DisplayedPhenotypicField[] = [
  'naturalLanguageQuery',
  'participantsAppliedType',
  'sqlQuery'
]

const GENOTYPIC_FIELD_LABELS: Record<DisplayedGenotypicField, string> = {
  filterJobName: 'Filter job',
  filterJobId: 'Filter job ID',
  queryMarkers: 'Query markers',
  genotypes: 'Genotypes'
}

const GENOTYPIC_FIELD_ORDER: DisplayedGenotypicField[] = ['filterJobName', 'queryMarkers', 'genotypes', 'filterJobId']

/** Cohort-browser `AppliedQueryType` → custodian-facing label. */
const PARTICIPANTS_APPLIED_TYPE_LABELS: Record<string, string> = {
  Standard: 'Standard query',
  Sql: 'SQL query',
  Tables: 'Tables query',
  OmicsSql: 'Omics SQL query',
  ClinicalSql: 'Clinical SQL query',
  DatahubSql: 'Datahub SQL query',
  DatahubAiSql: 'Datahub AI SQL query',
  DatahubReset: 'Datahub reset'
}

const formatParticipantsAppliedType = (value: string): string | undefined => PARTICIPANTS_APPLIED_TYPE_LABELS[value]

const formatQueryParameterValue = (field: string, value: QueryParameterValue): string | undefined => {
  if (field === 'participantsAppliedType') {
    return typeof value === 'string' ? formatParticipantsAppliedType(value) : undefined
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : undefined
  }

  return value?.trim() ? value : undefined
}

const getQueryDisplayRows = <TField extends string>(
  query: Partial<Record<TField, QueryParameterValue>> | undefined,
  fieldOrder: TField[],
  fieldLabels: Record<TField, string>
): DataAccessRequestQueryDisplayRow[] => {
  if (!query) {
    return []
  }

  return fieldOrder.flatMap(field => {
    const value = formatQueryParameterValue(field, query[field])

    if (!value) {
      return []
    }

    return [{ field, label: fieldLabels[field], value }]
  })
}

/**
 * Only the explicit `false` proves the request is unrestricted. A missing flag (legacy v1 requests, a
 * partially populated snapshot, an unmapped field) is unknown, not unrestricted, so nothing is claimed.
 */
const getIsUnrestricted = (dataAccessRequest: DataAccessRequestQuerySource): boolean =>
  dataAccessRequest.hasParticipantRestrictions === false

export interface DataAccessRequestParticipantCount {
  /** Participants the request reaches: the cohort match, or every participant when unrestricted. */
  matched: number
  total: number
}

const getParticipantCount = (
  dataAccessRequest: DataAccessRequestQuerySource,
  isUnrestricted: boolean
): DataAccessRequestParticipantCount | undefined => {
  const { cohort } = dataAccessRequest

  if (
    !cohort ||
    typeof cohort.numberOfParticipants !== 'number' ||
    typeof cohort.totalNumberOfParticipants !== 'number'
  ) {
    return undefined
  }

  return {
    matched: isUnrestricted ? cohort.totalNumberOfParticipants : cohort.numberOfParticipants,
    total: cohort.totalNumberOfParticipants
  }
}

export interface GetDataAccessRequestQueryDisplayRowsReturn {
  participantCount?: DataAccessRequestParticipantCount
  phenotypicRows: DataAccessRequestQueryDisplayRow[]
  genotypicRows: DataAccessRequestQueryDisplayRow[]
  isUnrestricted: boolean
}

export const getDataAccessRequestQueryDisplayRows = (
  dataAccessRequest: DataAccessRequestQuerySource
): GetDataAccessRequestQueryDisplayRowsReturn => {
  const phenotypicRows = getQueryDisplayRows(
    dataAccessRequest.query?.phenotypic,
    PHENOTYPIC_FIELD_ORDER,
    PHENOTYPIC_FIELD_LABELS
  )
  const genotypicRows = getQueryDisplayRows(
    dataAccessRequest.query?.genotypic,
    GENOTYPIC_FIELD_ORDER,
    GENOTYPIC_FIELD_LABELS
  )
  const isUnrestricted = getIsUnrestricted(dataAccessRequest)

  return {
    participantCount: getParticipantCount(dataAccessRequest, isUnrestricted),
    phenotypicRows,
    genotypicRows,
    isUnrestricted
  }
}
